<template>
  <n-dropdown
    placement="bottom-start"
    trigger="manual"
    :x="menuX"
    :y="menuY"
    :options="menuOptions"
    :show="menuVisible"
    @select="handleSelect"
    @clickoutside="handleClickOutside"
  />
  <feature-tree-node-context-menu
    v-model:show="assemblyContextMenuVisible"
    :x="assemblyContextMenuX"
    :y="assemblyContextMenuY"
    :target-key="assemblyContextMenuTargetKey"
    :revision="assemblyContextMenuRevision"
    :readonly-preview="readonlyPreview"
  />
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import type { Subscription } from 'rxjs';
import { useMessage, type DropdownOption } from 'naive-ui';
import {
  ContextMenuActionType,
  ContextMenuScopeType,
  EditorPanelShowPropertyReason,
  Viewer,
} from '@puppycad/cad-core';
import type { ContextMenuOpenEventPayload } from '@puppycad/cad-core';
import { useViewer } from '@/hooks/useViewer';
import { notifySectionViewUiStateChanged } from '@/utils';
//------ 特征面草图绘制 开始------
import { useFeatureTreeStore } from '@/hooks/useFeatureTree';
import { resolveViewportAssemblyContextMenuTarget } from '@/hooks/feature-tree/resolve-viewport-assembly-context-menu-target';
import FeatureTreeNodeContextMenu from '@/components/editor/FeatureTreeNodeContextMenu.vue';
import { requestEditorPropertyTab } from './requestEditorPropertyTab';
//------ 特征面草图绘制 结束------

const READONLY_PREVIEW_EDITING_MESSAGE = '历史版本仅支持查看。如需编辑，请基于此版本继续编辑。';

/**
 * 视口右键菜单组件属性。
 */
interface EditorViewportContextMenuProps {
  /**
   * 是否处于历史版本只读预览态。
   */
  readonlyPreview?: boolean;
}

/**
 * 视口右键菜单组件事件。
 */
interface EditorViewportContextMenuEmits {
  /**
   * 删除动作已执行完成。
   * @param event 事件名。
   */
  (event: 'deleted'): void;
}

const props = withDefaults(defineProps<EditorViewportContextMenuProps>(), {
  readonlyPreview: false,
});
const emit = defineEmits<EditorViewportContextMenuEmits>();
const message = useMessage();
const { getViewer } = useViewer();
//------ 特征面草图绘制 开始------
const featureTreeStore = useFeatureTreeStore();

/**
 * 只读历史预览态下会写入 CAD 主状态的右键菜单动作集合。
 */
const READONLY_PREVIEW_BLOCKED_ACTIONS = new Set<ContextMenuActionType>([
  ContextMenuActionType.Delete,
  ContextMenuActionType.InsertSketch,
  ContextMenuActionType.SectionViewProperties,
]);
//------ 特征面草图绘制 结束------

/** 菜单显示状态。 */
const menuVisible = ref(false);
/** 菜单 X 坐标。 */
const menuX = ref(0);
/** 菜单 Y 坐标。 */
const menuY = ref(0);
/** 菜单动作集合。 */
const menuActions = ref<ContextMenuActionType[]>([]);
/** 当前右键菜单载荷中的选中对象 ID 列表。 */
const selectedObjectIds = ref<string[]>([]);
/** 当前右键菜单载荷中的显式草图尺寸 ID 列表。 */
const selectedSketchDimensionIds = ref<string[]>([]);
/** 右键菜单打开时命中的正式基准面 ID。 */
const selectedDatumPlaneId = ref<number | null>(null);
/** 装配组件共享设计树菜单显示状态。 */
const assemblyContextMenuVisible = ref(false);
/** 装配组件共享菜单 X 坐标。 */
const assemblyContextMenuX = ref(0);
/** 装配组件共享菜单 Y 坐标。 */
const assemblyContextMenuY = ref(0);
/** 视口命中的装配组件对应的设计树节点 key。 */
const assemblyContextMenuTargetKey = ref('');
/** 同一装配组件连续右键时用于强制共享菜单重读树节点状态。 */
const assemblyContextMenuRevision = ref(0);
/** 右键菜单订阅。 */
const contextMenuSubscription = ref<Subscription | null>(null);
/** Viewer 静态创建事件订阅。 */
const viewerCreatedSubscription = ref<Subscription | null>(null);

/**
 * 解析菜单动作标签。
 *
 * @param action 菜单动作。
 * @returns 标签文本。
 */
const resolveActionLabel = (action: ContextMenuActionType): string => {
  if (action === ContextMenuActionType.Delete) {
    return '删除';
  }
  if (action === ContextMenuActionType.InsertSketch) {
    return '草图绘制';
  }
  if (action === ContextMenuActionType.NormalToFace) {
    return '正视于';
  }
  if (action === ContextMenuActionType.SectionViewProperties) {
    return '剖面视图属性';
  }
  return '';
};

/**
 * 菜单选项。
 */
const menuOptions = computed<DropdownOption[]>(() => {
  return menuActions.value
    .map((action) => ({
      label: resolveActionLabel(action),
      key: action,
    }))
    .filter((option) => option.label.length > 0);
});

/**
 * 判断右键菜单动作是否在只读历史预览态下被阻断。
 *
 * @param action 右键菜单动作。
 * @returns 只读历史预览态下是否需要阻断。
 */
const isReadonlyPreviewBlockedAction = (action: ContextMenuActionType): boolean => {
  return props.readonlyPreview && READONLY_PREVIEW_BLOCKED_ACTIONS.has(action);
};

/**
 * 解析只读预览态允许展示的右键菜单动作。
 *
 * @param actions cad-core 派发的原始动作集合。
 * @returns 当前编辑态可展示的动作集合。
 */
const resolveVisibleContextMenuActions = (actions: ContextMenuActionType[]): ContextMenuActionType[] => {
  if (!props.readonlyPreview) {
    return actions;
  }

  /**
   * 菜单展示与 select 硬阻断共用同一个只读阻断集合，避免新增写状态动作时出现入口分叉。
   */
  return actions.filter((action) => !isReadonlyPreviewBlockedAction(action));
};

/**
 * 打开装配组件对应的设计树共享右键菜单。
 *
 * @param payload cad-core 派发的右键菜单载荷。
 */
function openAssemblyComponentContextMenu(payload: ContextMenuOpenEventPayload): void {
  const target = resolveViewportAssemblyContextMenuTarget({
    objectIds: payload.selectedObjectIds,
    assemblyNodeIds: payload.selectedAssemblyNodeIds,
    featureTreeNodeMap: featureTreeStore.featureTreeNodeMap,
  });
  if (!target) {
    closeMenu();
    closeAssemblyContextMenu();
    return;
  }

  closeMenu();
  assemblyContextMenuX.value = payload.clientX;
  assemblyContextMenuY.value = payload.clientY;
  assemblyContextMenuTargetKey.value = target.treeKey;
  assemblyContextMenuRevision.value += 1;
  assemblyContextMenuVisible.value = true;
}

/**
 * 关闭普通视口右键菜单并清理其命中对象。
 */
const closeMenu = (): void => {
  menuVisible.value = false;
  menuActions.value = [];
  selectedObjectIds.value = [];
  selectedSketchDimensionIds.value = [];
  selectedDatumPlaneId.value = null;
};

/**
 * 关闭装配组件共享右键菜单并清理设计树目标。
 */
const closeAssemblyContextMenu = (): void => {
  assemblyContextMenuVisible.value = false;
  assemblyContextMenuTargetKey.value = '';
};

/**
 * 处理右键菜单打开事件。
 *
 * @param payload 菜单上下文载荷。
 */
const handleContextMenuOpen = (payload: ContextMenuOpenEventPayload): void => {
  if (payload.scope === ContextMenuScopeType.AssemblyComponent) {
    openAssemblyComponentContextMenu(payload);
    return;
  }

  closeAssemblyContextMenu();
  menuX.value = payload.clientX;
  menuY.value = payload.clientY;
  const visibleActions = resolveVisibleContextMenuActions([...payload.actions]);
  menuActions.value = visibleActions;
  selectedObjectIds.value = [...payload.selectedObjectIds];
  selectedSketchDimensionIds.value = [...payload.selectedSketchDimensionIds];
  selectedDatumPlaneId.value = payload.selectedDatumPlaneId;
  menuVisible.value = visibleActions.length > 0;
};

/**
 * 绑定右键菜单订阅。
 *
 * @param viewer Viewer 实例。
 */
const bindContextMenuSubscription = (viewer: Viewer): void => {
  contextMenuSubscription.value?.unsubscribe();
  contextMenuSubscription.value = null;
  const openObservable = viewer.observeContextMenuOpen?.();
  if (!openObservable) {
    return;
  }
  contextMenuSubscription.value = openObservable.subscribe((payload) => {
    handleContextMenuOpen(payload);
  });
};

/**
 * 处理普通视口右键菜单项选择。
 *
 * @param key 选中的 cad-core 菜单动作。
 */
const handleSelect = (key: string | number): void => {
  const action = String(key) as ContextMenuActionType;
  if (isReadonlyPreviewBlockedAction(action)) {
    message.warning(READONLY_PREVIEW_EDITING_MESSAGE);
    closeMenu();
    return;
  }

  const viewer = getViewer();
  if (!viewer) {
    closeMenu();
    return;
  }

  if (action === ContextMenuActionType.Delete) {
    if (
      selectedObjectIds.value.length <= 0
      && selectedSketchDimensionIds.value.length <= 0
    ) {
      message.warning('当前未命中可删除对象');
      closeMenu();
      return;
    }
    const removed = viewer.executeContextMenuAction?.(ContextMenuActionType.Delete) ?? false;
    if (!removed) {
      message.warning('当前上下文不支持删除');
      closeMenu();
      return;
    }
    closeMenu();
    emit('deleted');
    return;
  }

  if (action === ContextMenuActionType.InsertSketch) {
    const datumPlaneId = selectedDatumPlaneId.value;
    closeMenu();
    if (datumPlaneId !== null) {
      /**
       * 正式基准面必须携带右键打开时的目标 ID，直接复用设计树同一动作入口。
       * 禁止退回通用 sketch-enter 待选流程，否则会丢失右键已经确定的草图宿主。
       */
      featureTreeStore.handleEnterDatumPlaneSketch(String(datumPlaneId));
      return;
    }
    /**
     * FeatureFace 没有正式基准面 ID，继续通过通用命令读取平面面快照并进入草图。
     */
    const handleSketchCommand = featureTreeStore.getHandleSketchCommand();
    if (handleSketchCommand) {
      handleSketchCommand('sketch-enter');
    } else {
      message.warning('草图命令处理器未就绪');
    }
    return;
  }

  if (action === ContextMenuActionType.NormalToFace) {
    closeMenu();
    /**
     * 正视于只改变相机视图，不修改模型主状态；具体几何判断由 cad-core 收口。
     */
    const adjusted = viewer.executeContextMenuAction?.(ContextMenuActionType.NormalToFace) ?? false;
    if (!adjusted) {
      message.warning('当前仅支持正视于平面面或基准面');
    }
    return;
  }

  if (action === ContextMenuActionType.SectionViewProperties) {
    closeMenu();
    // 属性重入仍由 cad-core ContextMenuController 执行，UI 只负责切换 PropertyManager。
    const opened = viewer.executeContextMenuAction?.(ContextMenuActionType.SectionViewProperties) ?? false;
    if (!opened) {
      message.warning('当前没有可编辑的剖面视图定义');
      return;
    }
    requestEditorPropertyTab(
      EditorPanelShowPropertyReason.SectionViewCommandOpened,
      null,
      0,
    );
    notifySectionViewUiStateChanged();
    return;
  }

  closeMenu();
};

/**
 * 处理点击菜单外区域。
 */
const handleClickOutside = (): void => {
  closeMenu();
};

onMounted(() => {
  const currentViewer = getViewer();
  if (currentViewer) {
    bindContextMenuSubscription(currentViewer);
  }

  viewerCreatedSubscription.value = Viewer.viewerCreated$.subscribe((viewer) => {
    bindContextMenuSubscription(viewer);
  });
});

onUnmounted(() => {
  contextMenuSubscription.value?.unsubscribe();
  contextMenuSubscription.value = null;
  viewerCreatedSubscription.value?.unsubscribe();
  viewerCreatedSubscription.value = null;
});
</script>
